// File: IPDPatientDischargePrint.js
import React from "react";
import { FaPrint } from "react-icons/fa";
import { getHeaderHtml } from "../../../../util/headerUtil";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import AuthContext from "../../../../store/auth-context";

export const printIPDPatientDischarge = (patientData, treatments, authCtx, hospitalDataOverride = null) => {
  const hospitalData = hospitalDataOverride || authCtx?.hospitalData;
  if (/Mobi|Android|iPhone/i.test(navigator.userAgent)) {
    alert("Notice: Printing requires a physical printer connection. Print may not work directly from mobile devices.");
  }
  const printWindow = window.open("", "_blank", "width=900,height=650");
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>Treatment History - ${patientData.name}</title>
        <style>
          @page {
            margin: 0;
            size: auto;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            color: #000;
            font-size: 11px;
            line-height: 1.3;
          }
          .page-container {
            padding: 0;
          }
          .content-wrapper {
            padding: 10mm;
          }
          
          /* Header Section */
          .hospital-header {
            text-align: center;
            border-bottom: 2.5px solid #000;
            margin-bottom: 12px;
            padding-bottom: 8px;
          }
          .hospital-header h1 {
            margin: 0;
            text-transform: uppercase;
            font-size: 20px;
            font-weight: bold;
          }
          .hospital-header p {
            margin: 2px 0;
            font-size: 10px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .hospital-header .doctor-name {
            font-size: 14px;
            font-weight: bold;
            margin: 4px 0;
            text-transform: uppercase;
          }
          
          .doc-title {
            text-align: center;
            text-transform: uppercase;
            font-weight: bold;
            font-size: 13px;
            margin: 10px 0;
            text-decoration: underline;
          }

          /* Patient Info Box */
          .patient-box {
            border: 1px solid #ccc;
            padding: 8px;
            margin-bottom: 15px;
            border-radius: 6px;
          }
          .patient-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2px 30px;
          }
          .info-row { display: flex; font-size: 10.5px; }
          .info-label { font-weight: bold; width: 110px; }
          .info-val { flex: 1; }

          /* Main Table */
          table.main-table {
            width: 100%;
            border-collapse: collapse;
          }
          table.main-table thead th {
            border: 1px solid #000;
            padding: 6px;
            text-align: center;
            text-transform: uppercase;
            font-size: 10.5px;
            font-weight: bold;
          }
          table.main-table tbody td {
            border: 1px solid #000;
            padding: 5px;
            vertical-align: top;
            font-size: 10px;
          }

          .vitals-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2px 10px;
          }
          .vitals-grid b { font-weight: bold; }

          /* Footer Signatures */
          .footer-signs {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
            padding: 0 10px;
          }
          .sign-box {
            text-align: center;
            width: 40%;
          }
          .sign-line {
            border-top: 1.5px solid #000;
            padding-top: 4px;
            font-weight: bold;
            font-size: 11px;
          }

          .patient-info-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 5px;
            border-bottom: 1px dotted #ccc;
            padding-bottom: 3px;
          }
          .patient-info-row div { flex: 1; }
          .patient-info-row div:last-child { text-align: right; }

          /* Print adjustment for footer */
          @media print {
             .footer-signs {
                position: fixed;
                bottom: 15mm;
                left: 10mm;
                right: 10mm;
                width: calc(100% - 20mm);
                box-sizing: border-box;
             }
          }
        </style>
      </head>
      <body>
        <div class="page-container">
          <div class="content-wrapper">
            ${getHeaderHtml(hospitalData)}
            <div class="doc-title" style="margin-top: 20px;">IPD TREATMENT SHEET</div>
          
            <div style="margin-top: 15px; margin-bottom: 20px;">
              <div class="patient-info-row">
                <div><strong>PATIENT NAME:</strong> ${patientData.name.toUpperCase()}</div>
                <div><strong>ADM. DATE:</strong> ${patientData.admissionDate} ${patientData.admissionTime}</div>
              </div>
              <div class="patient-info-row">
                <div><strong>AGE / GENDER:</strong> ${patientData.age || "-"} YRS / ${patientData.gender || "-"}</div>
                <div><strong>IPD NO:</strong> ${patientData.ipdAdmissionId || "-"}</div>
              </div>
              <div class="patient-info-row">
                <div><strong>CONTACT:</strong> ${patientData.phone || "-"}</div>
                <div><strong>DOCTOR:</strong> ${(hospitalData.doctor || "-").toUpperCase()}</div>
              </div>
            </div>

          <table class="main-table">
            <thead>
              <tr>
                <th style="width: 25%">DATE & TIME</th>
                <th style="width: 35%">VITALS</th>
                <th style="width: 40%">MEDICATION / NOTES</th>
              </tr>
            </thead>
            <tbody>
              ${treatments.length > 0
      ? treatments.map(t => `
                  <tr>
                    <td style="text-align:center;"><b>${t.vitalDateTime ? t.vitalDateTime.split(" ")[0] : (t.date || "-")}</b><br/>${t.vitalDateTime ? (t.vitalDateTime.split(" ")[1] || "").slice(0, 5) : (t.time || "-")}</td>
                    <td>
                      <div class="vitals-grid">
                        <span><b>BP:</b> ${t.bpSystolic || "-"}/${t.bpDiastolic || "-"}</span>
                        <span><b>TEMP:</b> ${t.temperature || t.temp || "-"}</span>
                        <span><b>PULSE:</b> ${t.pulse || "-"}</span>
                        <span><b>SPO2:</b> ${t.spo2 || "-"}%</span>
                        <span><b>WT:</b> ${t.weight || "-"} kg</span>
                        <span><b>RR:</b> ${t.option1 || "-"}</span>
                      </div>
                    </td>
                    <td>
                      ${(t.complaint || t.remarks)
          ? `<div style="margin-bottom: 6px; padding: 4px; background: #f9f9f9; border: 0.5px solid #ddd; font-weight: bold;">
                             ${t.complaint ? `C: ${t.complaint}` : ""} ${t.remarks ? `<br/>R: ${t.remarks}` : ""}
                           </div>`
          : ""}
                      ${Array.isArray(t.details)
          ? (t.details.filter(d => d.name).length > 0
            ? t.details.filter(d => d.name).map(d => `
                                <div style="margin-bottom: 4px;">
                                  <b>${d.type ? d.type + ": " : ""}${d.name}</b> ${d.dose ? `(${d.dose})` : ""}<br/>
                                  <span style="font-size: 9px; font-style: italic;">${d.instructions || ""}</span>
                                </div>`).join("")
            : "-")
          : (t.details && t.details !== "N/A" ? t.details : "-")}
                    </td>
                  </tr>`).join("")
      : `<tr><td colspan="3" style="text-align:center;">No Treatments Recorded</td></tr>`}
            </tbody>
          </table>
          
          <div class="footer-signs">
            <div class="sign-box">
              <div class="sign-line">Patient / Guardian Signature</div>
            </div>
            <div class="sign-box">
              <div class="sign-line">Authority Sign / Doctor Sign</div>
              <div style="font-size: 10px; margin-top: 3px;">${hospitalData.doctor || ""}</div>
              ${(() => {
      const userRole = authCtx?.role;
      const userName = authCtx?.userName;
      if (userRole === "ROLE_DOCTOR" && userName) {
        return `<div style="font-size: 9px; margin-top: 2px;"><b>( ${userName} )</b></div>`;
      }
      return "";
    })()}
            </div>
          </div>
          </div>
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

const IPDPatientDischargePrint = ({ patientData, treatments, hospitalData }) => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  return (
    <button
      onClick={() => printIPDPatientDischarge(patientData, treatments, authCtx, hospitalData)}
      className="btn-premium"
    >
      <FaPrint className="me-2" /> {t("Print IPD Summary")}
    </button>
  );
};

export default IPDPatientDischargePrint;
